import React from 'react';
import { FlowNode } from '../../../../types/flowBuilder';

// Közös interface-ek
export interface BaseConfigProps {
  node: FlowNode;
  updateConfig: (key: string, value: any) => void;
}

// ConfigSection komponens
export const ConfigSection: React.FC<{
  title: string;
  children: React.ReactNode;
}> = ({ title, children }) => {
  return (
    <div className="space-y-3">
      <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700 pb-2">
        {title}
      </h4>
      <div className="space-y-3">
        {children}
      </div>
    </div>
  );
};

// ConfigField komponens
export const ConfigField: React.FC<{
  label: string;
  description?: string;
  children: React.ReactNode;
}> = ({ label, description, children }) => {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label}
      </label>
      {description && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
          {description}
        </p>
      )}
      {children}
    </div>
  );
};

// Helper függvények
export const getTemplatePreview = (templateType: string): string => {
  const templates: Record<string, string> = {
    welcome: 'Kedves {{customer.name}},\n\nÜdvözöljük a rendszerben!',
    confirmation: 'Kérése feldolgozásra került.\nReferencia: {{request.id}}',
    notification: 'Új esemény történt: {{event.type}}',
    reminder: 'Emlékeztető: {{reminder.message}}',
    custom: 'Egyedi sablon tartalma...'
  };
  return templates[templateType] || 'Sablon nem található';
};

// Közös CSS osztályok
export const commonStyles = {
  input: "w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 dark:bg-gray-700 dark:text-white",
  select: "w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-primary-500 focus:border-primary-500",
  textarea: "w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-primary-500 focus:border-primary-500 resize-none",
  label: "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1",
  smallLabel: "block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1",
  button: "px-3 py-1.5 bg-primary-600 text-white text-xs rounded-md hover:bg-primary-700 transition-colors",
  dangerButton: "px-3 py-1.5 bg-red-600 text-white text-xs rounded-md hover:bg-red-700 transition-colors",
  card: "bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-3",
  codeBlock: "bg-gray-900 dark:bg-black border border-gray-700 rounded-lg p-3"
};

// Validáció segédfüggvények
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const validateUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

export const validateCron = (cron: string): boolean => {
  // Egyszerű cron validáció (5 vagy 6 mező)
  const parts = cron.trim().split(/\s+/);
  return parts.length === 5 || parts.length === 6;
};

// Operátor opciók a feltételekhez
export const conditionOperators = [
  { value: '==', label: 'Egyenlő (==)' },
  { value: '!=', label: 'Nem egyenlő (!=)' },
  { value: '>', label: 'Nagyobb (>)' },
  { value: '<', label: 'Kisebb (<)' },
  { value: '>=', label: 'Nagy/egyenlő (>=)' },
  { value: '<=', label: 'Kis/egyenlő (<=)' },
  { value: 'contains', label: 'Tartalmazza' },
  { value: 'startsWith', label: 'Ezzel kezdődik' },
  { value: 'endsWith', label: 'Ezzel végződik' },
  { value: 'isEmpty', label: 'Üres' },
  { value: 'isNotEmpty', label: 'Nem üres' }
];

// HTTP metódusok
export const httpMethods = [
  { value: 'GET', label: 'GET' },
  { value: 'POST', label: 'POST' },
  { value: 'PUT', label: 'PUT' },
  { value: 'DELETE', label: 'DELETE' },
  { value: 'PATCH', label: 'PATCH' }
];

// Időegységek
export const timeUnits = [
  { value: 'seconds', label: 'Másodperc' },
  { value: 'minutes', label: 'Perc' },
  { value: 'hours', label: 'Óra' },
  { value: 'days', label: 'Nap' }
];

// Log szintek
export const logLevels = [
  { value: 'debug', label: 'Debug' },
  { value: 'info', label: 'Info' },
  { value: 'warn', label: 'Warning' },
  { value: 'error', label: 'Error' }
];
